import { take } from 'lodash/fp';
import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import vars from '../../../../style/base/vars.less';
import { pxToNumber } from '../../../common/services/number-formatter';

type BasicStep = {
    key: string;
};

type AddRefForStep = (stepKey: string, ref: Element | null) => void;

const addRefForStepContext = createContext<AddRefForStep>(() => {});

const headerHeight = pxToNumber(vars.headerHeight);
const baseOffset = headerHeight + pxToNumber(vars.standardpadding);

export const WizardScroller = ({
    currentStepKey,
    children,
}: {
    currentStepKey: string | null;
    children: React.ReactNode;
}) => {
    const prevStepKeyRef = useRef<string | null>(null);
    const stepRefs = useRef<Partial<{ [stepKey: string]: Element[] }>>({});

    const addRefForStep = useCallback((stepKey: string, ref: Element | null) => {
        if (ref) {
            stepRefs.current[stepKey] = take(2, [ref, ...(stepRefs.current[stepKey] ?? [])]);
        }
    }, []);

    useEffect(() => {
        const prevStepKey = prevStepKeyRef.current;
        prevStepKeyRef.current = currentStepKey;

        if (!(currentStepKey && prevStepKey)) {
            return;
        }

        const [prevStepRefIncoming, prevStepRefOutgoing] = stepRefs.current[prevStepKey] ?? [];
        const [currentStepRefIncoming] = stepRefs.current[currentStepKey] ?? [];

        if (!(prevStepRefIncoming && prevStepRefOutgoing && currentStepRefIncoming)) {
            return;
        }

        const prevStepRectIncoming = prevStepRefIncoming.getBoundingClientRect();
        const prevStepRectOutgoing = prevStepRefOutgoing.getBoundingClientRect();
        const currentStepRectIncoming = currentStepRefIncoming.getBoundingClientRect();

        if (prevStepRectIncoming.top < baseOffset) {
            const offset = prevStepRectIncoming.height - prevStepRectOutgoing.height;
            const scrollBy = currentStepRectIncoming.top - baseOffset + offset;

            window.scrollBy({
                top: scrollBy,
                behavior: 'smooth',
            });
        }
    }, [currentStepKey]);

    return <addRefForStepContext.Provider value={addRefForStep}>{children}</addRefForStepContext.Provider>;
};

const useStepRef = (step: BasicStep) => {
    const addRefForStep = useContext(addRefForStepContext);
    return useCallback((ref: Element | null) => addRefForStep(step.key, ref), [addRefForStep, step.key]);
};

export const ScrollStep = ({ step, children }: { step: BasicStep; children: React.ReactNode }) => {
    const stepRef = useStepRef(step);
    return <div ref={stepRef}>{children}</div>;
};
